/**
 * ============================================================================
 * CapaWorkbenchStats — four hero KPI tiles (Phase 7 CAPA).
 * Owner: Phase 7 — CAPA Module agent.
 * URS rows: CAPA-011 (overdue surface), CAPA-020 (KPIs).
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './CapaWorkbench.module.css';
import type { CapaWorkbenchStats as Stats } from './useCapasQuery';

export interface CapaWorkbenchStatsProps {
  stats: Stats | undefined;
  isLoading: boolean;
}

export function CapaWorkbenchStats({ stats, isLoading }: CapaWorkbenchStatsProps) {
  const { t } = useTranslation();

  if (isLoading || !stats) {
    return (
      <div className={styles.statsRow} aria-hidden="true">
        <div className={styles.statSkeleton} />
        <div className={styles.statSkeleton} />
        <div className={styles.statSkeleton} />
        <div className={styles.statSkeleton} />
      </div>
    );
  }

  return (
    <div className={styles.statsRow}>
      <Tile
        label={t('capa.stats.totalOpen', { defaultValue: 'Open CAPAs' })}
        value={stats.totalOpen}
        footnote={t('capa.stats.totalOpenNote', {
          defaultValue: 'Status ≠ CLOSED, CANCELLED.',
        })}
      />
      <Tile
        label={t('capa.stats.criticalOpen', {
          defaultValue: 'Critical open',
        })}
        value={stats.criticalOpen}
        footnote={t('capa.stats.criticalNote', {
          defaultValue: '30-day SLA per CAPA-004.',
        })}
        dot="#EF4444"
      />
      <Tile
        label={t('capa.stats.overdue', { defaultValue: 'Overdue' })}
        value={stats.overdue}
        footnote={t('capa.stats.overdueNote', {
          defaultValue: 'Past due_date_utc per CAPA-011.',
        })}
        dot="#F59E0B"
      />
      <Tile
        label={t('capa.stats.closedEff', {
          defaultValue: 'Closed effective (30d)',
        })}
        value={stats.closedEffectiveThisMonth}
        footnote={t('capa.stats.closedEffNote', {
          defaultValue: 'Outcome=EFFECTIVE per CAPA-007.',
        })}
        dot="#1DD1A1"
      />
    </div>
  );
}

function Tile({
  label,
  value,
  footnote,
  dot,
}: {
  label: string;
  value: number;
  footnote?: string;
  dot?: string;
}) {
  return (
    <div className={styles.statTile}>
      <div className={styles.statTileHeader}>
        <span className={styles.statTileLabel}>{label}</span>
        {dot && (
          <span
            className={styles.statDot}
            style={{ background: dot }}
            aria-hidden="true"
          />
        )}
      </div>
      <span className={styles.statTileValue}>{value}</span>
      {footnote && <span className={styles.statTileFootnote}>{footnote}</span>}
    </div>
  );
}
